<template>
    <div
        :id="id"
        class="profile-card"
        :class="{
            'profile-card--has-actions': $slots['actions'],
            'profile-card--skeleton': loading,
            'profile-card--link': !!linkHref,
            [`profile-card--${borderStyle}`]: !!borderStyle,
        }"
    >
        <Component
            :is="linkHref ? 'a' : 'div'"
            :href="linkHref"
            :target="linkTarget"
            :aria-label="altText"
            class="profile-card__inner"
            @click="$emit('click', $event)"
        >
            <header class="profile-card__header">
                <VJoyWrapper no-margin row-gap="2" column-gap="2" justify="space-between" class="profile-card__header-top">
                    <div>
                        <slot name="badges">
                            <ProfileAvailabilityBadge
                                v-if="!compactMode && profile.availability"
                                :status="profile.availability.status"
                                :work-availability="profile.availability.workAvailability"
                                :next-availability-date="profile.availability.nextAvailabilityDate"
                                :frequency="profile.availability.frequency"
                            />
                        </slot>
                    </div>
                    <VJoyWrapper v-if="$slots['extra-actions']" no-margin row-gap="1" column-gap="1" align="center">
                        <slot name="extra-actions" />
                    </VJoyWrapper>
                </VJoyWrapper>
                <PortraitProfileHeaderBackground
                    v-if="!loading"
                    :photo-url="profile.photoUrl"
                    :alt="altText"
                    :portfolio="!compactMode ? profile.portfolio : []"
                    @portfolio-clicked="$emit('portfolio-clicked')"
                />
            </header>
            <div class="profile-card__body">
                <div class="profile-card__body__content">
                    <VJoyWrapper no-margin row-gap="1" direction="column" align="flex-start">
                        <slot v-if="$slots['top-name']" name="top-name" />
                        <ProfileName
                            :first-name="profile.firstName"
                            :last-name-normalized="profile.lastNameNormalized"
                            :badges="profile.badges"
                            :vertical="profile.vertical"
                        >
                            <slot v-if="$slots['extra-name']" name="extra-name" />
                        </ProfileName>
                        <ProfileHeadline v-if="profile.headline" :headline="profile.headline" />
                        <ProfileInformation
                            :price="profile.price"
                            :location="profile.location"
                            :stats="profile.stats"
                            :global-matching-enabled="!!globalMatchingEnabled"
                            :first-name="profile.firstName"
                            :target-city="profile.targetCity"
                            :compact-mode="compactMode"
                            :tooltip-price="tooltipPrice"
                        />
                    </VJoyWrapper>

                    <ProfileSkills v-if="profile.skills.length > 0" :skills="profile.skills" :max-lines="2" class="joy-mt-4" />

                    <VJoyWrapper
                        v-if="$slots['main-body']"
                        no-margin
                        row-gap="1"
                        column-gap="1"
                        direction="column"
                        align="flex-start"
                        class="joy-mt-4"
                    >
                        <slot name="main-body" />
                    </VJoyWrapper>

                    <VJoyWrapper
                        v-if="$slots['secondary-body']"
                        no-margin
                        row-gap="1"
                        column-gap="1"
                        direction="column"
                        align="flex-start"
                        class="joy-mt-4 profile-card__extra-body"
                    >
                        <slot name="secondary-body" />
                    </VJoyWrapper>
                </div>
            </div>
        </Component>

        <VJoyWrapper v-if="$slots['actions']" no-margin column-gap="2" wrap="nowrap" class="profile-card__actions">
            <slot name="actions" />
        </VJoyWrapper>
    </div>
</template>

<script setup lang="ts">
    import {computed, type VNode} from 'vue';
    import {VJoyWrapper} from '@maltjoy/core-vue';
    import PortraitProfileHeaderBackground from './PortraitProfileHeaderBackground.vue';
    import ProfileAvailabilityBadge from '../common/ProfileAvailability.vue';
    import ProfileHeadline from '../common/ProfileHeadline.vue';
    import ProfileInformation from '../common/ProfileInformation.vue';
    import ProfileName from '../common/ProfileName.vue';
    import ProfileSkills from '../common/ProfileSkills.vue';
    import type {ProfileCardProps} from '../types';

    const props = defineProps<ProfileCardProps>();

    defineEmits<{
        click: [event: Event];
        'portfolio-clicked': [];
    }>();

    defineSlots<{
        badges?: () => VNode;
        'top-name'?: () => VNode;
        'extra-name'?: () => VNode;
        'main-body'?: () => VNode;
        'secondary-body'?: () => VNode;
        'extra-actions'?: () => VNode;
        actions?: () => VNode[];
    }>();

    const altText = computed(() => `${props.profile.firstName} ${props.profile.lastNameNormalized} - ${props.profile.headline}`);
</script>

<style lang="scss" scoped>
    @keyframes shine {
        to {
            background-position-x: -200%;
        }
    }

    $action-button-half-height: 33px;

    .profile-card {
        position: relative;

        &:hover {
            .profile-card__actions {
                box-shadow: var(--joy-core-elevation-2);
            }
        }

        &--has-actions {
            margin-bottom: calc($action-button-half-height - 1px);

            .profile-card__body__content {
                padding-bottom: calc(var(--joy-core-spacing-3) + $action-button-half-height);
            }
        }

        &--ADMIN {
            .profile-card__inner {
                border-color: var(--joy-color-quaternary-50);
            }

            .profile-card__actions {
                border-color: var(--joy-color-quaternary-50);
            }
        }

        &--SELECTED {
            .profile-card__inner {
                border-color: var(--joy-color-secondary-50);
            }

            .profile-card__actions {
                border-color: var(--joy-color-secondary-50);
            }
        }

        &--link {
            .profile-card__inner {
                transition: box-shadow var(--joy-transition-duration-short);
            }

            &:hover .profile-card__inner {
                box-shadow: var(--joy-core-elevation-2);
            }

            &:has(.profile-card__actions:hover):hover .profile-card__inner {
                box-shadow: none;
            }
        }

        &--skeleton {
            .profile-card__inner {
                background-color: var(--joy-color-neutral-10);
                border-color: var(--joy-color-neutral-30);
            }

            .profile-card__header {
                background: linear-gradient(110deg, var(--joy-color-neutral-20) 8%, var(--joy-color-neutral-10) 18%, var(--joy-color-neutral-20) 33%);
                background-size: 200% 100%;
                animation: 1.5s shine linear infinite;
                border: 0;
            }

            .profile-card__body,
            .profile-card__header > * {
                visibility: hidden;
            }

            .profile-card__actions {
                border-color: var(--joy-color-neutral-30);

                &:hover {
                    box-shadow: none;
                }

                & > * {
                    visibility: hidden;
                }
            }
        }

        &__inner {
            display: flex;
            flex-direction: column;
            flex: 1 0 auto;
            background-color: var(--joy-color-neutral-0);
            border-radius: var(--joy-core-radius-4);
            border: 1px solid var(--joy-color-neutral-30);
            text-decoration: none;
            color: inherit;
            overflow: hidden;
            height: 100%;
        }

        &__header {
            flex-grow: 0;
            aspect-ratio: 3 / 2;
            position: relative;
            z-index: 0;
            padding: var(--joy-core-spacing-2) var(--joy-core-spacing-3);
            overflow: hidden;
        }

        &__body {
            flex-grow: 1;
            position: relative;
            margin-top: -1px;
            color: var(--joy-color-text-heading);

            &__content {
                padding: var(--joy-core-spacing-3);
            }
        }

        &__actions {
            position: absolute;
            bottom: 0;
            left: 50%;
            background-color: var(--joy-color-neutral-0);
            transform: translate(-50%, 50%);
            padding: calc(var(--joy-core-spacing-2) - 1px);
            border: 1px solid var(--joy-color-neutral-30);
            border-radius: 48px;
            transition: box-shadow var(--joy-transition-duration-short);

            &:hover {
                box-shadow: var(--joy-core-elevation-2);
            }
        }
    }
</style>
